"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react"

interface ConsentBannerProps {
  onAccept: () => void
  onDecline: () => void
}

export const ConsentBanner: React.FC<ConsentBannerProps> = ({ onAccept, onDecline }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Card className="w-full border-2 border-primary/20 bg-card/95 backdrop-blur-lg shadow-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">Try our demo experience</CardTitle>
        <CardDescription className="text-sm">
          We use device fingerprinting to remember your usage across sessions. This helps us prevent abuse and doesn&apos;t track you for advertising.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          Learn more about what data we collect
        </button>

        {isExpanded && (
          <div className="space-y-2 p-3 bg-muted/50 rounded-lg text-xs">
            <p className="font-medium">What data is collected:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
              <li>Browser type and version</li>
              <li>Screen resolution</li>
              <li>Hardware identifiers (canvas, WebGL)</li>
            </ul>
            <p className="font-medium mt-2">Purpose:</p>
            <p className="text-muted-foreground">
              Creates a unique ID for demo tracking only. This helps us prevent abuse and ensure
              fair usage. The data is not shared with third parties and is not used for advertising
              purposes.
            </p>
            <p className="font-medium mt-2">Privacy:</p>
            <p className="text-muted-foreground">
              For more details, please read our{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Privacy Policy
                <ExternalLink className="h-3 w-3" />
              </a>
              .
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button onClick={onAccept} className="flex-1" size="default">
            Accept & Try Demo
          </Button>
          <Button onClick={onDecline} variant="outline" className="flex-1" size="default">
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ConsentDeclinedProps {
  onSignup: () => void
  onAccept: () => void
}

export const ConsentDeclined: React.FC<ConsentDeclinedProps> = ({ onSignup, onAccept }) => {
  return (
    <Card className="w-full border-2 border-muted bg-card/95 backdrop-blur-lg shadow-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">Demo requires consent</CardTitle>
        <CardDescription className="text-sm">
          To try our interactive demo, we need your consent to use device fingerprinting for usage
          limiting. This helps us provide a fair experience for all users.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Button onClick={onAccept} className="w-full" size="default">
          Accept & Try Demo
        </Button>
        <Button onClick={onSignup} variant="outline" className="w-full" size="default">
          Sign up instead
        </Button>
      </CardContent>
    </Card>
  )
}

